import { useState, useEffect } from "react";
import { HandCoins } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtBRL, fmtDate } from "../lib/format";

const input = "bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
const GENERAL = "__general__";

export const ApplyCreditForm = ({ clientId, currentCredit, onApplied }) => {
  const [open, setOpen] = useState(null);
  const [target, setTarget] = useState(GENERAL);
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/clients/${clientId}/open-orders`)
      .then((r) => setOpen(r.data))
      .catch((err) => toast.error(apiError(err)));
  }, [clientId]);

  const selected = open?.orders.find((o) => o.order_id === target);
  const targetBalance = target === GENERAL ? (open?.total_balance ?? 0) : (selected?.balance ?? 0);
  const maxApply = Math.max(0, Math.min(currentCredit, targetBalance));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/credits/apply`, {
        amount: amount ? parseFloat(amount) : null,
        order_id: target === GENERAL ? null : target,
      });
      const pedidos = data.orders.map((o) => `#${o.order_code} (${fmtBRL(o.applied)})`).join(", ");
      toast.success(`${fmtBRL(data.applied)} de crédito aplicado em ${pedidos} — crédito restante: ${fmtBRL(data.client_credit)}`, { duration: 8000 });
      setAmount("");
      onApplied && onApplied(data);
    } catch (err) {
      toast.error(apiError(err));
    }
    setSaving(false);
  };

  if (!open) return <p className="text-xs text-zinc-500">Carregando pedidos em aberto...</p>;
  if (open.orders.length === 0) {
    return <p data-testid="apply-credit-no-orders" className="text-xs text-zinc-400">Este cliente não possui pedidos em aberto — o crédito continua disponível para pedidos futuros.</p>;
  }

  return (
    <form onSubmit={submit} data-testid="apply-credit-form" className="border border-sky-400/30 rounded p-4 space-y-3">
      <p className="text-xs uppercase tracking-[0.15em] text-sky-400">Aplicar Crédito em Saldo Devedor — auditado</p>
      <div>
        <label className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 block mb-1">Aplicar em</label>
        <select data-testid="apply-credit-target-select" className={`${input} w-full`} value={target}
          onChange={(e) => { setTarget(e.target.value); setAmount(""); }}>
          <option value={GENERAL}>Reduzir saldo devedor geral — {fmtBRL(open.total_balance)} (do pedido mais antigo ao mais novo)</option>
          {open.orders.map((o) => (
            <option key={o.order_id} value={o.order_id}>
              Pedido #{o.order_code} · {fmtDate(o.order_date)}{o.nf_number ? ` · NF ${o.nf_number}` : ""} · saldo {fmtBRL(o.balance)}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 block mb-1">Valor a aplicar (R$)</label>
          <input data-testid="apply-credit-amount-input" type="number" step="0.01" min="0.01" max={maxApply}
            className={`${input} w-full`} placeholder={`Máx: ${fmtBRL(maxApply)}`}
            value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <button data-testid="apply-credit-submit-button" disabled={saving || maxApply <= 0}
          className="flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded transition-colors">
          <HandCoins size={15} weight="bold" /> {saving ? "Aplicando..." : "Aplicar Crédito"}
        </button>
      </div>
      <p className="text-[11px] text-zinc-500">
        Em branco = aplica o máximo ({fmtBRL(maxApply)}). Registra um pagamento "Crédito do Cliente" no(s) pedido(s) e uma linha de saída no extrato, com data, valor, pedido e quem aplicou.
      </p>
    </form>
  );
};
